import React, {
  forwardRef,
  useCallback,
  useId,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactElement,
} from "react";
import ReactDatePicker, {
  type ReactDatePickerCustomHeaderProps,
} from "react-datepicker";
import { CalendarDays, X } from "lucide-react";
import clsx from "clsx";

import { useSyncConstraintValidationMessage } from "../../../hooks/use-sync-constraint-validation-message.ts";

import "react-datepicker/dist/react-datepicker.css";
import { DatePickerCalendarHeader } from "./date-picker-calendar-header.tsx";
import styles from "./date-picker.module.css";

/** Portal root id for calendar popper (must match CSS). */
export const V2_DATEPICKER_PORTAL_ID = "v2-datepicker-portal";

let v2DatepickerPortalEnsured = false;

/**
 * Ensures `#v2-datepicker-portal` exists on `document.body` so date pickers can portal
 * without editing `public/index.html`. Safe to call on every mount (idempotent).
 */
export function ensureV2DatepickerPortal(): void {
  if (v2DatepickerPortalEnsured || typeof document === "undefined") return;
  if (document.getElementById(V2_DATEPICKER_PORTAL_ID)) {
    v2DatepickerPortalEnsured = true;
    return;
  }
  const el = document.createElement("div");
  el.id = V2_DATEPICKER_PORTAL_ID;
  document.body.appendChild(el);
  v2DatepickerPortalEnsured = true;
}

export type DatePickerProps = {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  dateFormat?: string;
  placeholderText?: string;
  customInput: ReactElement;
  /** Shows a clear control when a date is selected. */
  isClearable?: boolean;
  /** Inclusive lower bound (calendar day). Omit for no minimum. */
  minDate?: Date;
  maxDate?: Date;
  label?: string;
  /** `stacked` = label above field (default). `inline` = label left, field right (one row). */
  labelLayout?: "stacked" | "inline";
  id?: string;
  /** Native required validation on an overlay proxy (button triggers cannot). */
  required?: boolean;
  /** Synced to `setCustomValidity` for Yup/custom messages (native tooltip). */
  error?: string;
  /** Calendar renders here via `document.getElementById`; defaults to {@link V2_DATEPICKER_PORTAL_ID}. */
  portalId?: string;
  /**
   * When true (default), calendar header uses v2 month/year selects (bounded by min/max) plus
   * prev/next month controls instead of changing month only via arrows.
   */
  showMonthYearDropdowns?: boolean;
  /** Ordering used by the year dropdown. Defaults to newest year first. */
  yearOrder?: "asc" | "desc";
};

export type DatePickerFieldTriggerProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "type" | "children"
> & {
  placeholderText?: string;
};

export const DatePickerFieldTrigger = forwardRef<
  HTMLButtonElement,
  DatePickerFieldTriggerProps
>(function DatePickerFieldTrigger(
  { placeholderText = "Select Date", className, value, ...rest },
  ref,
) {
  const hasValue = value != null && value !== "";

  return (
    <button
      ref={ref}
      type="button"
      className={clsx(styles.fieldTrigger, className)}
      {...rest}
    >
      <CalendarDays
        size={18}
        strokeWidth={2}
        className={styles.fieldTriggerIcon}
        aria-hidden
      />
      <span
        className={clsx(
          styles.fieldTriggerLabel,
          !hasValue && styles.fieldTriggerLabelPlaceholder,
        )}
      >
        {hasValue ? value : placeholderText}
      </span>
    </button>
  );
});

DatePickerFieldTrigger.displayName = "DatePickerFieldTrigger";

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function DatePicker({
  selected,
  onChange,
  disabled = false,
  dateFormat = "MMM d, yyyy",
  placeholderText = "Select Date",
  customInput,
  minDate: minDateProp,
  maxDate: maxDateProp,
  label,
  labelLayout = "stacked",
  id: idProp,
  required = false,
  error,
  portalId = V2_DATEPICKER_PORTAL_ID,
  showMonthYearDropdowns = true,
  yearOrder = "desc",
  isClearable = false,
}: DatePickerProps) {
  if (portalId) {
    ensureV2DatepickerPortal();
  }
  const reactId = useId().replace(/:/g, "");
  const fieldId = idProp ?? `datepicker-${reactId}`;
  const proxyRef = useRef<HTMLInputElement | null>(null);
  const today = startOfDay(new Date());
  // const maxDate = maxDateProp != null ? startOfDay(maxDateProp) : today;
  const maxDate = maxDateProp != null ? startOfDay(maxDateProp) : undefined;
  const minDate = minDateProp != null ? startOfDay(minDateProp) : undefined;

  useSyncConstraintValidationMessage(proxyRef, error);

  const renderCalendarHeader = useCallback(
    (headerProps: ReactDatePickerCustomHeaderProps) => (
      <DatePickerCalendarHeader
        {...headerProps}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        yearOrder={yearOrder}
      />
    ),
    [minDate, maxDate, disabled, yearOrder],
  );

  const picker = (
    <div className={clsx(styles.fieldRow, error && styles.error)}>
      <div className={styles.wrapper}>
        <ReactDatePicker
          id={fieldId}
          selected={selected ?? undefined}
          onChange={(date) => {
            onChange(date ?? null);
          }}
          disabled={disabled}
          dateFormat={dateFormat}
          placeholderText={placeholderText}
          customInput={customInput}
          minDate={minDate}
          maxDate={maxDate}
          renderCustomHeader={
            showMonthYearDropdowns ? renderCalendarHeader : undefined
          }
          popperClassName={styles.popper}
          portalId={portalId}
          popperProps={{ strategy: "fixed" }}
        />
        <input
          ref={proxyRef}
          type="text"
          className={styles.proxyInput}
          tabIndex={-1}
          aria-hidden
          required={required}
          value={selected != null ? "filled" : ""}
          onChange={() => {}}
        />
      </div>
      {isClearable && selected != null && !disabled ? (
        <button
          type="button"
          className={styles.clearButton}
          aria-label="Clear Date"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
          }}
        >
          <X size={16} strokeWidth={2} aria-hidden />
        </button>
      ) : null}
    </div>
  );

  if (!label) {
    return picker;
  }

  const inline = labelLayout === "inline";

  return (
    <div className={clsx(styles.root, inline && styles.rootInline)}>
      <label
        className={clsx(
          "sub-heading-md",
          styles.label,
          inline && styles.labelInline,
        )}
        htmlFor={fieldId}
      >
        {label}
        {required ? (
          <span className="text-danger text-nowrap" aria-hidden>
            {"\u00A0*"}
          </span>
        ) : null}
      </label>
      <div className={inline ? styles.control : styles.controlPassThrough}>
        {picker}
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  );
}
